/**
 * Display-state rules for the "Övrigt" (Other) chips/inputs in
 * VehicleInformationSection. Kept as pure functions so they are unit-testable in
 * the node vitest environment (no jsdom) - same style as the selector tests.
 *
 * Background (display-only bug, no data loss): for a lookup whose make matched NO
 * catalog entry (e.g. plate LFO419 -> make "Ligier", model "JS50"), the
 * auto-select-Övrigt effect never ran (it required a catalog make with models), so
 * the fallback branch rendered an Övrigt chip HARD-styled as selected with the
 * free-text input collapsed and empty - even though the value was correctly in
 * form state. These rules make the input auto-expand pre-filled and the chip
 * highlight honest.
 */

/**
 * Pre-existing rule (unchanged semantics): an auto-loaded model that a KNOWN
 * catalog make's chip list cannot represent auto-selects Övrigt with the value
 * pre-filled.
 */
export const shouldAutoExpandOtherForUnlistedModel = (args: {
  isAutoLoaded: boolean;
  model: string | undefined;
  hasCatalogMake: boolean;
  isLoadingMakes: boolean;
  availableModelNames: string[];
  showOtherInput: boolean;
}): boolean => {
  const {
    isAutoLoaded,
    model,
    hasCatalogMake,
    isLoadingMakes,
    availableModelNames,
    showOtherInput,
  } = args;
  if (
    !isAutoLoaded ||
    !model ||
    !hasCatalogMake ||
    isLoadingMakes ||
    availableModelNames.length === 0 ||
    showOtherInput
  ) {
    return false;
  }
  return !availableModelNames.some(
    (name) => name.toLowerCase().trim() === model.toLowerCase().trim(),
  );
};

/**
 * Fix: an auto-loaded lookup whose make matched NO catalog entry. The catalog can
 * offer no chips at all, so a present value must expand the Övrigt input
 * pre-filled (mirroring the unlisted-model rule above). Only applies once the
 * makes catalog has settled, and never re-triggers while the input is open.
 */
export const shouldAutoExpandOtherForUnknownMake = (args: {
  isAutoLoaded: boolean;
  value: string | undefined;
  hasCatalogMake: boolean;
  isLoadingMakes: boolean;
  makesLoaded: boolean;
  showOtherInput: boolean;
}): boolean => {
  const {
    isAutoLoaded,
    value,
    hasCatalogMake,
    isLoadingMakes,
    makesLoaded,
    showOtherInput,
  } = args;
  return Boolean(
    isAutoLoaded &&
      value &&
      !hasCatalogMake &&
      !isLoadingMakes &&
      makesLoaded &&
      !showOtherInput,
  );
};

/**
 * The fallback Övrigt chip (unknown make / make without models) may only look
 * selected when a value actually exists in form state; an empty value renders the
 * standard unselected styling.
 */
export const isFallbackOtherChipSelected = (
  value: string | undefined,
): boolean => Boolean(value);

/**
 * Övrigt free-text submit contract: returns the trimmed value, or null when there
 * is nothing to submit (whitespace-only input never submits).
 */
export const normalizeOtherSubmitValue = (text: string): string | null => {
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
};
